import {AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild} from "@angular/core";
import {autoUnsubscribe} from "../../../core/decorators/auto-unsubscribe.decorator";
import {MAT_DIALOG_DATA, MatDialog} from "@angular/material/dialog";
import {CompareFilesDialog, CompareFilesDialogData} from "./compare-files.dialog";
import {first, tap, zip} from "rxjs";
import {MatTable} from "@angular/material/table";
import {FileUtilsService} from "../../../core/services/file-utils.service";
import {ModelVersion} from "../../../core/models/model-version";
import {ModelService} from "../../../core/api/model.service";
import {environment} from "../../../../environments/environment";
import { autoMonitorPageView } from "src/app/core/decorators/auto-monitor-page-view.decorator";
import { MonitoringService } from "src/app/core/services/monitoring.service";


export interface CompareModelsDialogData {
  leftVersion: ModelVersion,
  rightVersion: ModelVersion
}



interface FileComparison {
  idx: number,
  leftPath: string,
  leftName: string,
  leftSize: number,
  leftFileId: string,
  leftFolder: string,
  leftDate: Date,
  rightPath: string,
  rightName: string,
  rightSize: number,
  rightFileId: string,
  rightFolder: string,
  rightDate: Date,
  changeType: 'no-change' | 'modified' | 'deleted' | 'added',
  comparisonViewable: boolean
}

type ComparisonSide = 'left' | 'right';

interface ChangeSummary {
  added: number;
  deleted: number;
  modified: number;
  unchanged: number;
}


@Component({
  standalone: false,
  selector: 'app-compare-models-dialog',
  templateUrl: './compare-models.dialog.html',
  styleUrls: ['./compare-models.dialog.scss']
})
@autoMonitorPageView({ name: 'Compare Models Dialog', trackOnInitToAfterInit: false })
@autoUnsubscribe({autoInclude: true})
export class CompareModelsDialog implements OnInit, AfterViewInit, OnDestroy{
  inProgress = true;
  fileComparisons: FileComparison[] = [];
  comparisonTableScrollWidth = 0;
  summary: ChangeSummary = {
    added: 0,
    deleted: 0,
    modified: 0,
    unchanged: 0
  };
  displayedColumns: string[] = ['idx', 'status', 'leftName', 'leftFolder', 'leftSize', 'leftDate', 'rightName', 'rightFolder', 'rightSize', 'rightDate'];
  @ViewChild(MatTable) table: MatTable<FileComparison>;
  @ViewChild('comparisonTable', {read: ElementRef}) comparisonTable: ElementRef<HTMLTableElement>;
  @ViewChild('comparisonTableContainer') comparisonTableContainer: ElementRef<HTMLDivElement>;
  @ViewChild('comparisonScrollbar') comparisonScrollbar: ElementRef<HTMLDivElement>;
  private syncScrollLock = false;


  constructor(@Inject(MAT_DIALOG_DATA) public data: CompareModelsDialogData, private modelsService: ModelService,
              private fileUtilsService: FileUtilsService,
              private dialog: MatDialog, private monitoringService: MonitoringService) {

  }
  ngAfterViewInit(): void {
    window.requestAnimationFrame(() => this.updateComparisonScrollWidth());
  }

  ngOnInit(): void {
    this.inProgress = true;
    this.fileComparisons = [];
    zip(
      this.modelsService.getFilesForModel(this.data.leftVersion.id).pipe(first()),
      this.modelsService.getFilesForModel(this.data.rightVersion.id).pipe(first())
    ).pipe(first(), tap(([leftModelFiles, rightModelFiles]) => {
      leftModelFiles.forEach(collFile => {
        const rightFile = rightModelFiles.find(p => collFile.path === p.path);
        const comparisonViewable = environment.viewableExtensions.some(p => p === this.fileUtilsService.getFileExtension(collFile.path));
        const fileComparison: FileComparison = {
          idx: this.fileComparisons.length + 1,
          leftPath: collFile.path,
          leftName: this.fileUtilsService.getFileName(collFile.path),
          leftFileId: collFile.id,
          leftFolder: this.fileUtilsService.getParentFolderPath(collFile.path),
          leftSize: collFile.size / 1024,
          leftDate: collFile.fileModifiedDateTime,
          rightName: rightFile ? this.fileUtilsService.getFileName(rightFile.path) : this.fileUtilsService.getFileName(collFile.path),
          rightFileId: rightFile?.id,
          rightFolder: rightFile ? this.fileUtilsService.getParentFolderPath(rightFile.path) : this.fileUtilsService.getParentFolderPath(collFile.path),
          rightSize: rightFile ? rightFile.size / 1024 : collFile.size / 1024,
          rightPath: rightFile?.path ?? collFile.path,
          rightDate: rightFile?.fileModifiedDateTime ?? collFile.fileModifiedDateTime,
          changeType: this.getChangeType(rightFile, collFile),
          comparisonViewable
        };
        this.fileComparisons.push(fileComparison);
      });

      rightModelFiles.forEach(collFile => {
        const rightFileExist = this.fileComparisons.find(p => collFile.path === p.leftPath) as FileComparison;
        if(!rightFileExist) {
          const comparisonViewable = environment.viewableExtensions.some(p => p === this.fileUtilsService.getFileExtension(collFile.path));
          this.fileComparisons.push({
            idx: this.fileComparisons.length + 1,
            leftPath: null,
            leftName: null,
            leftFileId: null,
            leftFolder: null,
            leftSize: null,
            leftDate: null,
            rightName: this.fileUtilsService.getFileName(collFile.path),
            rightFileId: collFile.id,
            rightFolder: this.fileUtilsService.getParentFolderPath(collFile.path),
            rightSize: collFile.size / 1024,
            rightPath: collFile.path,
            rightDate: collFile.fileModifiedDateTime,
            changeType: 'added',
            comparisonViewable
          } as FileComparison);
        }
      });

      this.fileComparisons = this.fileComparisons
        .sort((a, b) => this.getChangePriority(a.changeType) - this.getChangePriority(b.changeType) || a.leftName?.localeCompare(b.leftName ?? '') || a.rightName?.localeCompare(b.rightName ?? ''));

      this.summary = this.fileComparisons.reduce((acc, comparison) => {
        switch (comparison.changeType) {
          case 'added':
            acc.added += 1;
            break;
          case 'deleted':
            acc.deleted += 1;
            break;
          case 'modified':
            acc.modified += 1;
            break;
          default:
            acc.unchanged += 1;
            break;
        }
        return acc;
      }, {
        added: 0,
        deleted: 0,
        modified: 0,
        unchanged: 0
      } as ChangeSummary);

      this.inProgress = false;
      this.table.renderRows();
      window.requestAnimationFrame(() => this.updateComparisonScrollWidth());
    })).subscribe();
  }

  ngOnDestroy(): void {
    console.log('on destroy CompareModelsDialog');
  }

  get leftVersionLabel(): string {
    return `Version ${this.data.leftVersion.versionNumber}`;
  }

  get rightVersionLabel(): string {
    return `Version ${this.data.rightVersion.versionNumber}`;
  }

  getCellClasses(comparison: FileComparison, side: ComparisonSide): Record<string, boolean> {
    return {
      modified: comparison.changeType === 'modified',
      deleted: comparison.changeType === 'deleted' && side === 'right',
      added: comparison.changeType === 'added' && side === 'right',
      muted: comparison.changeType === 'deleted' && side === 'left'
    };
  }

  syncComparisonScroll(source: 'top' | 'table'): void {
    if (this.syncScrollLock || !this.comparisonTableContainer?.nativeElement || !this.comparisonScrollbar?.nativeElement) {
      return;
    }

    const topScrollbar = this.comparisonScrollbar.nativeElement;
    const tableContainer = this.comparisonTableContainer.nativeElement;
    this.syncScrollLock = true;

    if (source === 'top') {
      tableContainer.scrollLeft = topScrollbar.scrollLeft;
    } else {
      topScrollbar.scrollLeft = tableContainer.scrollLeft;
    }

    window.requestAnimationFrame(() => this.syncScrollLock = false);
  }

  private updateComparisonScrollWidth(): void {
    if (!this.comparisonTable?.nativeElement) {
      return;
    }

    this.comparisonTableScrollWidth = this.comparisonTable.nativeElement.scrollWidth;
    if (this.comparisonScrollbar?.nativeElement) {
      this.comparisonScrollbar.nativeElement.scrollLeft = this.comparisonTableContainer.nativeElement.scrollLeft;
    }
  }

  private getChangePriority(changeType: FileComparison['changeType']): number {
    switch (changeType) {
      case 'modified':
        return 0;
      case 'added':
        return 1;
      case 'deleted':
        return 2;
      default:
        return 3;
    }
  }

  private getChangeType(rightFile, collFile)
  {
     if(!rightFile) {
       return 'deleted';
     }

     return (collFile.md5 === rightFile.md5)? 'no-change': 'modified';
  }

  onCompareFileVersions(fileName: string, leftFileId: string, rightFileId: string) {
    this.dialog.open(CompareFilesDialog, {
      width: '80vw',
      height: '80vh',
      data: {
        title: `${fileName}`,
        leftFileId: leftFileId,
        rightFileId: rightFileId
      } as CompareFilesDialogData,
    });
  }

}
